import type { ChainArch, TsState } from "@opensea/graphql"
import { isAddressEqual, normalizeAddress } from "@opensea/utils"
import { proxy, subscribe } from "valtio"
import { deepClone } from "valtio/utils"
import { AccountNotConnectedError } from "../polychain/lib/errors"
import type { Address } from "../types"
import { persistStore, restoreStore } from "../utils/storeUtils"
import { PreviousAccountsActions } from "./previousAccountsStore"

// -- Helpers -------------------------------------------------------
const STORAGE_KEY = "opensea-wallet-account-store"
const STORAGE_VERSION = 1

function getSafeChainId(account: SetAccountInput) {
  if (Number.isNaN(account.chainId)) {
    // For Flow wallet, return chainId of flow mainnet as default
    if (account.connectorId === "com.flowfoundation.wallet") {
      return 747
    }
    // Default to 1 (Ethereum mainnet) for other wallets
    return 1
  }
  return account.chainId
}

function normalizeAccountInput(accountInput: SetAccountInput) {
  return {
    ...accountInput,
    address: normalizeAddress(accountInput.address),
    addresses: accountInput.addresses.map(normalizeAddress),
  }
}

// -- Types ---------------------------------------------------------
interface AccountStateType {
  storageVersion: typeof STORAGE_VERSION
  addresses: readonly Address[]
  tsStates: TsState[]
  isConnecting: boolean
  authenticated: boolean
  ready: boolean
  shouldSyncAccountState: boolean
  chainArch: ChainArch
  address?: Address
  chainId?: number
  connectorId?: string
  hasAcceptedTerms?: boolean // undefined means not loaded yet
  externalAccount?: SetAccountInput
}

type DisconnectedAccountStateType = Pick<
  AccountStateType,
  | "address"
  | "addresses"
  | "chainId"
  | "connectorId"
  | "tsStates"
  | "hasAcceptedTerms"
  | "externalAccount"
>

export interface SetAccountInput {
  address: Address
  chainId: number
  connectorId: string
  addresses: readonly Address[]
  authenticated?: boolean
  ready?: boolean
}

// -- State ---------------------------------------------------------
const DISCONNECTED_STATE: DisconnectedAccountStateType = {
  address: undefined,
  addresses: [],
  chainId: undefined,
  connectorId: undefined,
  tsStates: [],
  hasAcceptedTerms: undefined,
  externalAccount: undefined,
}

const INITIAL_STATE: AccountStateType = {
  ...DISCONNECTED_STATE,
  storageVersion: STORAGE_VERSION,
  isConnecting: true,
  ready: false,
  shouldSyncAccountState: true,
  chainArch: "EVM",
  authenticated: false,
}

export const AccountState = proxy<AccountStateType>(deepClone(INITIAL_STATE))

// -- Actions -------------------------------------------------------
export const AccountActions = {
  rehydrate() {
    return Object.assign(
      AccountState,
      restoreStore(STORAGE_KEY, STORAGE_VERSION, INITIAL_STATE),
    )
  },

  setExternalAccount(account?: SetAccountInput) {
    AccountState.externalAccount = account
  },

  setAccount(account?: SetAccountInput) {
    if (account) {
      Object.assign(AccountState, {
        isConnecting: false,
        ...normalizeAccountInput(account),
        chainId: getSafeChainId(account),
      })
      PreviousAccountsActions.addAccount({
        ...normalizeAccountInput(account),
        chainArch: AccountState.chainArch,
      })
    } else {
      Object.assign(AccountState, {
        ...DISCONNECTED_STATE,
        ready: true,
        isConnecting: false,
      })
    }
  },

  setIsConnecting(isConnecting: AccountStateType["isConnecting"]) {
    if (!AccountState.shouldSyncAccountState) {
      return
    }
    if (!isConnecting) {
      AccountState.isConnecting = false
      return
    }
    Object.assign(AccountState, {
      ...DISCONNECTED_STATE,
      address: AccountState.address,
      addresses: AccountState.addresses,
      isConnecting,
      ready: AccountState.ready,
      chainArch: AccountState.chainArch,
    })
  },

  setAuthenticated(authenticated: AccountStateType["authenticated"]) {
    AccountState.authenticated = authenticated
    AccountState.ready = true
  },

  setReady() {
    AccountState.ready = true
  },

  setTsStates(tsStates: AccountStateType["tsStates"]) {
    AccountState.tsStates = tsStates
  },

  setHasAcceptedTerms(hasAcceptedTerms: boolean) {
    AccountState.hasAcceptedTerms = hasAcceptedTerms
  },

  setActiveChainArch(chainArch: AccountStateType["chainArch"]) {
    AccountState.chainArch = chainArch
  },

  switchAddress(address: Address) {
    if (!AccountState.addresses.some(a => isAddressEqual(a, address))) {
      throw new AccountNotConnectedError(
        "Attempted to switch to an address that is not connected",
      )
    }
    AccountState.address = address
  },

  setShouldSyncAccountState(
    shouldSyncAccountState: AccountStateType["shouldSyncAccountState"],
  ) {
    AccountState.shouldSyncAccountState = shouldSyncAccountState
  },
}

// -- Subscribe -----------------------------------------------------
subscribe(AccountState, () => {
  persistStore(STORAGE_KEY, {
    ...AccountState,
    isConnecting: true, // Start in a connecting state
    ready: false,
    hasAcceptedTerms: AccountState.hasAcceptedTerms || undefined,
    chainArch: AccountState.chainArch,
    shouldSyncAccountState: true,
    authenticated: false,
  })
})
